import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PushPin, Trash, Plus } from "@phosphor-icons/react";
import { fmtDateTime, fmtAgo, NOTE_CATEGORY_TONES, humanize } from "./format";

const CATEGORIES = ["general", "urgent", "qc", "production", "finance"];

function Card({ title, children, testId, action }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={testId}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/30">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{title}</h3>
                {action}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

export function NotesTab({ c, reloadCase }) {
    const [notes, setNotes] = useState([]);
    const [text, setText] = useState("");
    const [category, setCategory] = useState("general");
    const [pinned, setPinned] = useState(false);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("all");

    const fetch = () => {
        api.get(`/admin/cases/${c.id}/internal-notes`)
            .then((r) => setNotes(r.data.notes || []))
            .catch(() => {});
    };
    useEffect(fetch, [c.id]);

    const submit = async () => {
        if (!text.trim()) { toast.error("Note can't be empty"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/cases/${c.id}/internal-notes`, {
                note: text.trim(), category, is_pinned: pinned,
            });
            setText(""); setPinned(false); setCategory("general");
            toast.success("Note added");
            fetch();
            reloadCase?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const togglePin = async (id) => {
        try {
            await api.put(`/admin/cases/${c.id}/internal-notes/${id}/pin`);
            fetch();
        } catch (e) { toast.error("Failed"); }
    };
    const remove = async (id) => {
        if (!confirm("Delete this note?")) return;
        try {
            await api.delete(`/admin/cases/${c.id}/internal-notes/${id}`);
            fetch();
            reloadCase?.();
        } catch (e) { toast.error("Failed"); }
    };

    const filtered = notes.filter((n) => filter === "all" ? true : n.category === filter);

    return (
        <div className="space-y-5">
            <Card title="Add internal note" testId="notes-composer">
                <div className="space-y-3">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Internal-only note (not visible to the doctor or patient)…"
                        rows={3}
                        className="w-full p-3 text-sm rounded-lg border border-border bg-background"
                        data-testid="notes-text"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="inline-flex rounded-full border border-border bg-card overflow-hidden">
                            {CATEGORIES.map((k) => {
                                const t = NOTE_CATEGORY_TONES[k];
                                const active = category === k;
                                return (
                                    <button
                                        key={k}
                                        onClick={() => setCategory(k)}
                                        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition ${active ? `${t.bg} ${t.text}` : "text-muted-foreground hover:bg-secondary"}`}
                                        data-testid={`note-category-${k}`}
                                    >{t.label}</button>
                                );
                            })}
                        </div>
                        <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
                            <PushPin size={12} weight={pinned ? "fill" : "regular"} /> Pin to top
                        </label>
                        <button
                            onClick={submit}
                            disabled={busy || !text.trim()}
                            className="ml-auto inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50"
                            data-testid="notes-submit"
                        >
                            <Plus size={12} weight="bold" /> Add note
                        </button>
                    </div>
                </div>
            </Card>

            <Card
                title={`Notes (${filtered.length})`}
                testId="notes-list"
                action={
                    <div className="inline-flex rounded-full border border-border bg-card overflow-hidden">
                        <button onClick={() => setFilter("all")} className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${filter === "all" ? "bg-foreground text-white" : "text-muted-foreground"}`}>All</button>
                        {CATEGORIES.map((k) => (
                            <button key={k} onClick={() => setFilter(k)} className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${filter === k ? "bg-foreground text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                                {NOTE_CATEGORY_TONES[k].label}
                            </button>
                        ))}
                    </div>
                }
            >
                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                        {filter === "all" ? "No internal notes yet — add the first one above." : `No ${NOTE_CATEGORY_TONES[filter]?.label} notes.`}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((n) => {
                            const t = NOTE_CATEGORY_TONES[n.category] || NOTE_CATEGORY_TONES.general;
                            return (
                                <div key={n.id} className={`p-3 rounded-lg border ${n.is_pinned ? "border-primary/30 bg-primary/5" : "border-border bg-card"}`} data-testid={`note-${n.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${t.bg} ${t.text}`}>{t.label}</span>
                                            {n.is_pinned && (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-primary font-bold uppercase tracking-wider"><PushPin size={10} weight="fill" /> Pinned</span>
                                            )}
                                            <span className="text-xs text-muted-foreground">{n.author_name || "Admin"} · {fmtAgo(n.created_at)}</span>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button onClick={() => togglePin(n.id)} className="p-1 rounded hover:bg-secondary text-muted-foreground" title="Pin / unpin">
                                                <PushPin size={13} weight={n.is_pinned ? "fill" : "regular"} />
                                            </button>
                                            <button onClick={() => remove(n.id)} className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Delete">
                                                <Trash size={13} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-1.5 text-sm whitespace-pre-wrap">{n.note}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}
